import * as React from "react";
import {
  CREDIT_APPLICATION_WIDGET_URL,
  saveOnboardData,
} from "../../libs/onboard";
import FileUploader from "../core/FileUploader";
import produce from "immer";
import jfetch from "../../utils/jfetch";
import CenterDiv from "../core/CenterDiv";
import * as Sentry from "@sentry/browser";

interface IProps {
  resetChoice: () => void;
  hardwareData?: IOnboardDataAttributes["hardwareData"];
  hardwareFiles?: IOnboardDataAttributes["hardwareFiles"];
  updateHardwareData: (data: IOnboardDataAttributes["hardwareData"]) => void;
  fetchHardwareData: () => void;
  org: IOrganizationRecord;
  createCWTicket: (acceptedFiles: File[], origin: string) => void;
}

interface IState {}

class HardwarePurchase extends React.Component<IProps, IState> {
  componentDidMount() {
    window.addEventListener("message", this.handleEchoSignEvent, false);
  }

  componentWillUnmount() {
    window.removeEventListener("message", this.handleEchoSignEvent);
  }

  handleEchoSignEvent = (e: MessageEvent) => {
    if (e.origin.includes("echosign.com")) {
      let data;
      try {
        data = JSON.parse(e.data);
      } catch (err) {
        Sentry.captureException(err);
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      }
      if (data && data.type === "ESIGN") {
        this.onEchoSignComplete();
      }
    }
  };

  onEchoSignComplete = () => {
    const { hardwareData, updateHardwareData } = this.props;
    setTimeout(async () => {
      updateHardwareData(
        produce(hardwareData, draft => {
          draft!.signedCreditApplication = true;
        })
      );
    });
  };

  resetEchoSign = () => {
    const { hardwareData, updateHardwareData } = this.props;
    updateHardwareData(
      produce(hardwareData, draft => {
        draft!.signedCreditApplication = false;
      })
    );
  };

  onFileDrop = async (acceptedFiles: File[]) => {
    const data = new FormData();

    acceptedFiles.forEach(file => data.append("hardware_files[]", file));

    return await jfetch
      .post<IJsonApiResponse<IOnboardDataRecord>>(
        `${this.props.org.links.url}/onboard_data/upload_hardware_files`,
        data
      )
      .then(resp => {
        this.props.fetchHardwareData();
        this.props.createCWTicket(acceptedFiles, "Purchase");
      });
  };

  render() {
    const { hardwareFiles, hardwareData } = this.props;
    return (
      <>
        <div className="panel panel-primary">
          <div className="panel-heading">Hardware Purchases</div>
          <div className="panel-body">
            <p>
              You've chosen to purchase hardware from CharTec. We'll need some
              documents to make this possible:
            </p>
            <ul>
              <li>CharTec Credit Application & Agreement</li>
              <li>
                Resale Certificate for your State
                <ul>
                  <li>Can be found online.</li>
                  <li>
                    Not required for Alaska, Oregon, Montana, New Hampshire, or
                    Canada.
                  </li>
                  <li>
                    Mississippi, Louisiana, and Florida Resale Certificates are
                    controlled by their respective states and will need to be
                    requested from your state office.
                  </li>
                </ul>
              </li>
            </ul>
            <p>
              If you'd like to go back and add HaaS or opt-out of hardware,
              please click the 'return' button below.
            </p>
            <button
              onClick={this.props.resetChoice}
              className={"btn btn-theme-primary"}
            >
              Return to Hardware Options
            </button>
          </div>
        </div>
        <div className="panel panel-info">
          <div className="panel-heading">Resale Certificate</div>
          <div className="panel-body">
            <CenterDiv>
              <p>Please upload your Resale Certificate here.</p>
            </CenterDiv>
            <FileUploader
              onDrop={this.onFileDrop}
              uploadedFiles={hardwareFiles || []}
            />
          </div>
        </div>
        <div className="panel panel-info">
          <div className="panel-heading">Credit Application and Agreement</div>
          <div className="panel-body">
            {!hardwareData || !hardwareData.signedCreditApplication ? (
              <>
                <CenterDiv>
                  <p>
                    Please fill out, sign, and submit the Credit Application and
                    Agreement below.
                  </p>
                </CenterDiv>
                <iframe
                  src={CREDIT_APPLICATION_WIDGET_URL}
                  width="100%"
                  height="800px"
                  frameBorder="0"
                  style={{
                    border: "0",
                    overflow: "hidden",
                    minWidth: "600px",
                  }}
                />
              </>
            ) : (
              <>
                <p>
                  You've submitted your signed Credit Application and Agreement!
                </p>
                <p>
                  If you need to sign it again, please click the button below:
                </p>
                <button
                  className={"btn btn-theme-primary"}
                  onClick={this.resetEchoSign}
                >
                  Sign Again
                </button>
              </>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default HardwarePurchase;



// WEBPACK FOOTER //
// ./app/javascript/components/onboarding/HardwarePurchase.tsx